<template>
  <div>
    <v-card class="mb-4 scrollable-card" :style="cardStyle">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="card-title">{{ props.title }}</span>
        <div class="breadcrumbs-container">
          <v-breadcrumbs 
            :items="breadcrumbs" 
            divider=">" 
            @click="handleBreadcrumbClick"
            class="text-subtitle-2 pa-0 breadcrumbs-wrapper"
            density="compact"
          ></v-breadcrumbs>
        </div>
      </v-card-title>
      <v-card-text>
        <v-chart class="chart" ref="chartRef" :style="{ height: chartHeight + 'px' }" :option="chartOption" :update-options="{ notMerge: true }" @click="handleChartClick" />
        
        <!-- Pagination controls - only shown when needed -->
        <div v-if="totalPages > 1" class="pagination-controls mt-2">
          <div class="d-flex align-center justify-space-between">
            <div class="text-body-2">
              {{ t('showing') }} {{ startItem }}-{{ endItem }} {{ t('of') }} {{ totalItems }} {{ t('items') }}
            </div>
            <div>
              <v-btn 
                size="small" 
                icon="mdi-chevron-left" 
                :disabled="currentPage === 1" 
                @click="changePage(currentPage - 1)"
                density="comfortable"
              ></v-btn>
              
              <span class="mx-2">{{ currentPage }} / {{ totalPages }}</span>
              
              <v-btn 
                size="small" 
                icon="mdi-chevron-right" 
                :disabled="currentPage === totalPages" 
                @click="changePage(currentPage + 1)"
                density="comfortable"
              ></v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed, watch, unref, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { TooltipComponent, LegendComponent, TitleComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";

use([CanvasRenderer, BarChart, TooltipComponent, LegendComponent, TitleComponent, GridComponent]);

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  chartFontColor: {
    type: String,
    default: '#000000'
  },
  chartCardHeight: {
    type: Number,
    default: 460
  },
  currentLevel: {
    type: [String, Number],
    default: 0
  },
  dataProviders: {
    type: Object,
    required: true
  },
  contextUpdaters: {
    type: Object,
    required: true
  },
  navigationConfig: {
    type: Array,
  },
  filterContext: {
      type: Object,
      required:true
  },
  dataLoaded: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n();

// Keep the parent's currentLevel binding in sync with internal navigation, so
// parents that short-circuit levels (by writing the bound ref) always see a change
const emit = defineEmits(['update:currentLevel']);

const chartRef = ref(null);
const chartOption = ref({});
const chartHeight = ref(props.chartCardHeight);
const currentLevel = ref(props.currentLevel);
const currentParent = ref(null);
const navigationState = ref({}); // Stores state for each level
const breadcrumbPath = ref([]);

// Pagination state
const currentPage = ref(1);
const itemsPerPage = ref(100); // Maximum number of bars to show at once
const totalItems = ref(0);
const fullDataset = ref([]); // Store the complete dataset

// Computed properties for pagination
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
const startItem = computed(() => (currentPage.value - 1) * itemsPerPage.value + 1);
const endItem = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalItems.value));

// Change page function
const changePage = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage;
    updateChartWithPagination();
  }
};

// Apply pagination to current dataset and update chart
const updateChartWithPagination = () => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = Math.min(start + itemsPerPage.value, totalItems.value);
  const paginatedData = fullDataset.value.slice(start, end);
  
  // Update chart with paginated data
  updateChartOption(paginatedData, true);
};

// The card grows with its content (pagination bounds it) so the page stays
// the only vertical scroll context — no nested card scrollbar
const cardStyle = computed(() => {
  return {
    'min-height': '460px',
    'height': 'auto',
    'display': 'flex',
    'flex-direction': 'column'
  };
});

// Initialize breadcrumb path based on navigation config
const initializeBreadcrumbs = () => {
  breadcrumbPath.value = [{ 
    title: t(props.navigationConfig[0].titleKey), 
    disabled: false, 
    href: '#',
    level: props.navigationConfig[0].level
  }];
};

const breadcrumbs = computed(() => {
  return breadcrumbPath.value.map((item, index) => ({
    ...item,
    disabled: index === breadcrumbPath.value.length - 1
  }));
});

const updateChartOption = (data, isPaginated = false) => {
  // If this is the first call with the full dataset, store it and apply pagination
  if (!isPaginated) {

    // Sort data by total value - we keep sorting even for paginated data
    // to ensure consistent ordering between pages
    data.sort((a, b) => {
      const totalA = Array.isArray(a.children) ? a.children.reduce((sum, status) => sum + status.value, 0) : a.value;
      const totalB = Array.isArray(b.children) ? b.children.reduce((sum, status) => sum + status.value, 0) : b.value;
      return totalB - totalA;
    });


    fullDataset.value = [...data]; // Store full dataset
    totalItems.value = data.length;
    
    // Reset to first page when new data is loaded
    currentPage.value = 1;
    
    // If data exceeds our limit, apply pagination immediately
    if (data.length > itemsPerPage.value) {
      const paginatedData = data.slice(0, itemsPerPage.value);
      updateChartOption(paginatedData, true);
      return;
    }
  }

  

  // Constants for chart sizing and thresholds
  const BAR_HEIGHT = 25;
  const BAR_GAP = 8;
  const AXIS_MARGIN = 80;
  const MIN_CHART_HEIGHT = 80;
  const ANIMATION_THRESHOLD = 30;
  
  const categories = data.map(item => item.name);
  const seriesData = {};

  // Process data for series
  data.forEach((item, index) => {
    if (Array.isArray(item.children)) {
      item.children.forEach(status => {
        if (!seriesData[status.color]) {
          seriesData[status.color] = { 
            id: status.color,
            name: Object.keys(status.statusCounts)[0],
            stack: 'Total',
            type: 'bar',
            data: Array(categories.length).fill(0),
            itemStyle: { color: status.color },
          };
        }
        seriesData[status.color].data[index] += status.value;
      });
    }
  });

  // Calculate height before setting options
  const calculatedHeight = Math.max(
    MIN_CHART_HEIGHT,
    (categories.length * BAR_HEIGHT) + 
    ((categories.length - 1) * BAR_GAP) + 
    AXIS_MARGIN
  );

  // Update height
  chartHeight.value = calculatedHeight;
  
  // Determine if we should use animation
  const shouldAnimate = categories.length <= ANIMATION_THRESHOLD;
  
  // Prepare chart options
  const maxValue = Math.max(...Object.values(seriesData).flatMap(series => series.data), 1);
  
  // Build the chart options directly
  const newChartOptions = {
    animation: shouldAnimate,
    animationDuration: shouldAnimate ? 800 : 0,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
      formatter: (params) => {
        let tooltip = '<div style="max-height: 300px; overflow-y: auto;">';
        tooltip += `<strong>${params[0].name}</strong><br/>`;
        let totalCount = 0;
        params.forEach(param => {
          if (param.value > 0) {
            const dataItem = data.find(item => item.name === param.name);
            const childSegment = dataItem?.children?.find(child => child.color === param.color);
            const statusCounts = childSegment?.statusCounts;

            if (statusCounts) {
              Object.entries(statusCounts).forEach(([statusTitle, count]) => {
                tooltip += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
                tooltip += `${statusTitle}: ${count}<br/>`;
                totalCount += count;
              });
            } else {
              // Fallback: use param.value directly if statusCounts not found
              tooltip += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
              tooltip += `${param.seriesName}: ${param.value}<br/>`;
              totalCount += param.value;
            }
          }
        });
        tooltip += `<strong>${t('total')}: ${totalCount}</strong>`;
        tooltip += '</div>';
        return tooltip;
      }
    },
    grid: {
      left: '3%',
      right: '4%', 
      bottom: '10px',
      top: '10px',
      containLabel: true
    },
    yAxis: {
      type: 'category',
      data: categories,
      inverse: true,
      axisTick: { alignWithLabel: true },
      axisLine: { show: false },
      axisLabel: {
        margin: 10,
        color: props.chartFontColor,
        fontSize: 12,
        overflow: 'truncate',
        width: 120
      }
    },
    xAxis: {
      type: 'value',
      axisLabel: { color: props.chartFontColor }
    },
    barCategoryGap: `${BAR_GAP}px`,
    barGap: '0%',
    series: Object.values(seriesData).map(series => ({
      ...series,
      barWidth: BAR_HEIGHT,
      encode: { x: 'value', y: 'category' },
      label: {
        show: true,
        position: 'inside',
        color: '#fff',
        formatter: (params) => {
          // Always show if value is large enough
          if (params.value > 100) return params.value;

          // For smaller values, check if bar segment is wide enough
          if (!chartRef.value) return '';

          const chartWidth = chartRef.value.getWidth();
          if (!chartWidth || chartWidth < 100) return '';

          // Account for grid margins (left 3% + right 4% = 7% of total width)
          const gridWidth = chartWidth * 0.93;

          // Calculate the pixel width of this bar segment
          const segmentWidth = (params.value / maxValue) * gridWidth;

          // Show label only if segment is wide enough (at least 45px)
          return segmentWidth > 45 ? params.value : '';
        }
      }
    }))
  };

  // Update the chart option
  chartOption.value = newChartOptions;

  // Handle resize after a short delay to ensure DOM is updated
  setTimeout(() => {
    if (chartRef.value && chartRef.value.resize) {
      try {
        chartRef.value.resize();
      } catch (error) {
        console.warn("Error resizing chart:", error);
      }
    }
    setupLabelAreaClicks();
  }, 200);
};

// Make the whole label gutter clickable: map any click left of the plot area
// to its row and drill into it (per-label hit areas are too small to rely on)
const setupLabelAreaClicks = () => {
  const chart = chartRef.value?.chart;
  if (!chart || chart.__labelClicksBound) return;
  chart.__labelClicksBound = true;

  // Returns the page-relative row index for a point in the label gutter, or null
  const rowIndexAt = (e) => {
    let gridLeft;
    try {
      gridLeft = chart.convertToPixel({ xAxisIndex: 0 }, 0);
    } catch (err) {
      return null;
    }
    if (typeof gridLeft !== 'number' || isNaN(gridLeft) || e.offsetX >= gridLeft) return null;

    const index = Math.round(chart.convertFromPixel({ yAxisIndex: 0 }, e.offsetY));
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const pageData = fullDataset.value.slice(start, start + itemsPerPage.value);
    return index >= 0 && index < pageData.length ? index : null;
  };

  chart.getZr().on('click', (e) => {
    const index = rowIndexAt(e);
    if (index !== null) {
      handleChartClick({ dataIndex: index });
    }
  });

  chart.getZr().on('mousemove', (e) => {
    if (rowIndexAt(e) !== null) {
      chart.getZr().setCursorStyle('pointer');
    }
  });
};

// Handle chart item clicks for drilling down
const handleChartClick = async (params) => {
  // Get the actual data item index accounting for pagination
  const adjustedItemIndex = (currentPage.value - 1) * itemsPerPage.value +
                           params.dataIndex;
                           
  // For drill-down, we need to work with the actual item from the full dataset
  const actualItem = fullDataset.value[adjustedItemIndex];
  
  if (!actualItem) return;
  
  // Get the actual name from the full dataset
  const actualName = actualItem.name;
  
  // Find current level index in navigation config
  const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
  
  // At the last level there's nowhere to drill down; instead, if the level
  // has a context updater (e.g. status), apply the clicked bar as a filter
  if (currentLevelIndex >= props.navigationConfig.length - 1) {
    const terminalUpdater = props.contextUpdaters[currentLevel.value];
    if (!terminalUpdater) return;

    navigationState.value[currentLevel.value] = {
      parentId: currentParent.value,
      selectedName: actualName
    };

    // Apply the filter first: filtersLastUpdated only ticks when the context
    // actually applied it, and a failed query must not leave the breadcrumbs
    // claiming a drill-down that never happened
    const updatedBefore = unref(props.filterContext.filtersLastUpdated);
    await terminalUpdater(actualItem.id, actualItem.parentId || null, actualItem.type || null);
    if (unref(props.filterContext.filtersLastUpdated) === updatedBefore) {
      console.error(`[${props.title}] "${actualName}" status filter was not applied — see query errors above`);
      return;
    }

    // Reflect the selection in the breadcrumbs; replace the previous terminal
    // selection instead of stacking crumbs at the same level
    const newCrumb = {
      title: actualName,
      disabled: false,
      href: '#',
      level: currentLevel.value,
      isTerminalSelection: true
    };
    const lastCrumb = breadcrumbPath.value[breadcrumbPath.value.length - 1];
    if (lastCrumb?.isTerminalSelection) {
      breadcrumbPath.value.splice(breadcrumbPath.value.length - 1, 1, newCrumb);
    } else {
      breadcrumbPath.value.push(newCrumb);
    }
    return;
  }
  
  // Get the next level in the navigation hierarchy
  const nextLevelConfig = props.navigationConfig[currentLevelIndex + 1];
  
  // Store relevant information for this level before changing
  navigationState.value[currentLevel.value] = {
    parentId: currentParent.value,
    selectedName: actualName,  // Use the actual name from full dataset
    ...(params.color ? { color: params.color } : {}),
    ...(params.seriesName ? { seriesName: params.seriesName } : {})
  };
  
  // Find the appropriate context updater for the current level
  const filterContextUpdater = props.contextUpdaters[currentLevel.value];
  const navigationConfigLevel = props.navigationConfig.find(nav => nav.level === currentLevel.value);
  
  // Update current level and parent references before updating filterContext
  currentLevel.value = nextLevelConfig.level;
  emit('update:currentLevel', nextLevelConfig.level);
  currentParent.value = navigationState.value[props.navigationConfig[currentLevelIndex].level]?.contextParam || null;
  
  // Update breadcrumb path
  const newBreadcrumb = { 
    title: actualName || params.seriesName || t(nextLevelConfig.titleKey), 
    disabled: false, 
    href: '#',
    level: nextLevelConfig.level
  };    
  breadcrumbPath.value.push(newBreadcrumb);  

  if (filterContextUpdater && navigationConfigLevel) {
    // The clicked item already carries id/parentId/type from the processed dataset —
    // names are not guaranteed unique, so never re-derive the id by name lookup
    const contextParam = actualItem.id;
    const parentIdParam = actualItem.parentId ? actualItem.parentId : null;
    const typeParam = actualItem.type ? actualItem.type : null;

    await filterContextUpdater(contextParam, parentIdParam, typeParam); //this will trigger refresh
  }
};

const handleBreadcrumbClick = async (e) => {
  const clickedBreadcrumb = breadcrumbPath.value.find(breadcrumb => breadcrumb.title === e.target.text);
  
  if (!clickedBreadcrumb) return;
  
  // Find the level in the navigation config
  const levelIndex = props.navigationConfig.findIndex(config => config.level === clickedBreadcrumb.level);
  
  if (levelIndex > 0) {
    // Get the corresponding level config
    const levelConfig = props.navigationConfig[levelIndex];
    
    // Restore the state for this level if available
    const levelState = navigationState.value[levelConfig.level];
    currentLevel.value = levelConfig.level;
    emit('update:currentLevel', levelConfig.level);
    currentParent.value = levelState?.parentId || null;
    
    // Truncate breadcrumb path to this level
    breadcrumbPath.value = breadcrumbPath.value.slice(0, levelIndex + 1);

    //Get data from level above so we know how to filter
    const prevLevelConfig = props.navigationConfig[levelIndex - 1];
    const currentLevelData = props.dataProviders[prevLevelConfig.level];
    const filterContextUpdater = props.contextUpdaters[prevLevelConfig.level];
    
    // Apply the appropriate context update
    if (currentLevelData && filterContextUpdater && levelState?.selectedName) {
      const levelData = await currentLevelData(false /*unfiltered*/);
      const selectedItem = levelData.find(item => item.name === clickedBreadcrumb.title);
      const contextParam = selectedItem?.id;
      const parentIdParam = selectedItem?.parentId ? selectedItem.parentId : null;
      const typeParam = selectedItem?.type ? selectedItem.type : null;

      await filterContextUpdater(contextParam, parentIdParam, typeParam);
    }
  } else {
    resetToTopLevel();
  }
};

const resetToTopLevel = async () => {
  const resetContext = props.contextUpdaters['reset'];
  if (resetContext) {
    resetContext();
  }
  
  currentLevel.value = props.navigationConfig[0].level;
  emit('update:currentLevel', props.navigationConfig[0].level);
  currentParent.value = null;
  navigationState.value = {};
  initializeBreadcrumbs();
  
  // Reset pagination state
  currentPage.value = 1;
  
  const dataProvider = props.dataProviders[currentLevel.value];
  if (dataProvider) {
    const data = await dataProvider();
    updateChartOption(data);
  }
};

const updateChartBasedOnCurrentLevel = async () => {
  // Reset pagination when updating based on current level
  currentPage.value = 1;
  
  // Get the appropriate data provider for the current level
  const dataProvider = props.dataProviders[currentLevel.value];
  
  if (!dataProvider) {
    console.error(`No data provider found for level: ${currentLevel.value}`);
    return;
  }

  try {
    const data = await dataProvider();
    if (data.length === 0) {
      console.warn(`[${props.title}] level "${currentLevel.value}" query returned 0 rows`);
    }
    updateChartOption(data);
  } catch (error) {
    console.error(`[${props.title}] error loading level "${currentLevel.value}":`, error);
  }
};

watch(
  () => props.filterContext.filtersLastUpdated,
  () => {
    if (isMounted.value) {
      updateChartBasedOnCurrentLevel();
    }
  },
  { deep: true }
);

// Watch for changes in chart font color
watch(() => props.chartFontColor, () => {
  updateChartBasedOnCurrentLevel();
});

watch(() => props.currentLevel, (newVal) => {
  currentLevel.value = newVal;
});

// Watch for page changes to update the chart
watch(() => currentPage.value, () => {
  updateChartWithPagination();
});

const isMounted = ref(false);
onMounted(async () => {
  currentLevel.value = props.navigationConfig[0].level
  emit('update:currentLevel', props.navigationConfig[0].level);
  initializeBreadcrumbs();
  isMounted.value = true;
  // Only load data if dataLoaded is already true
  if (props.dataLoaded) {
    await updateChartBasedOnCurrentLevel();
  }
});

// Watch for dataLoaded to become true and load initial data
watch(
  () => props.dataLoaded,
  (newVal) => {
    if (newVal && isMounted.value) {
      updateChartBasedOnCurrentLevel();
    }
  }
);
</script>

<style scoped>
.chart {
  width: 100%;
  height: 100%;
}

.scrollable-card {
  display: flex;
  flex-direction: column;
}

.scrollable-card :deep(.v-card-text) {
  flex: 1;
  padding-top:0;
  padding-bottom: 16px; /* Ensure some padding at bottom */
}

.card-title {
  flex: 0 1 auto;
  margin-right: 8px;
}

.breadcrumbs-container {
  flex: 1 1 auto;
  overflow-x: auto;
  max-width: 70%;
  white-space: nowrap;
  scrollbar-width: thin;
  display: flex;
  justify-content: flex-end;
}

.breadcrumbs-container::-webkit-scrollbar {
  height: 4px;
}

.breadcrumbs-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.breadcrumbs-wrapper {
  margin-left: auto;
}

.breadcrumbs-wrapper :deep(.v-breadcrumbs-item) {
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.breadcrumbs-wrapper :deep(.v-breadcrumbs__wrapper) {
  display: flex;
  flex-direction: row-reverse;
}

.pagination-controls {
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>